import React, { useState } from 'react';
import {
  X,
  Facebook,
  Twitter,
  Linkedin,
  Mail,
  MessageCircle,
  Send,
  Copy,
  Check,
  Share2
} from 'lucide-react';
import './ShareModal.css';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  description?: string;
  artist?: string;
  hashtags?: string[];
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  title,
  url,
  description,
  artist,
  hashtags = []
}) => {
  const [copied, setCopied] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  if (!isOpen) return null;

  const shareText = `🎵 ${title}${artist ? ` by ${artist}` : ''}\n\nListen on NaatCity`;
  const shareDescription = description || `Listen to this beautiful Islamic naat "${title}"${artist ? ` recited by ${artist}` : ''} on NaatCity.`;
  
  // Default hashtags
  const defaultHashtags = ['NaatCity', 'IslamicNaat', 'Naat', ...(hashtags || [])];
  const hashtagString = defaultHashtags.join(',');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: shareText,
          url: url,
        });
        setShareSuccess(true);
        setTimeout(() => {
          setShareSuccess(false);
          onClose();
        }, 1500);
      } catch (err) {
        // User cancelled or error occurred
        console.log('Share cancelled or failed:', err);
      }
    }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`,
    
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}&hashtags=${hashtagString}`,
    
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${url}`)}`,
    
    telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`,
    
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    
    email: `mailto:?subject=${encodeURIComponent(`${title}${artist ? ` - ${artist}` : ''} | NaatCity`)}&body=${encodeURIComponent(`${shareDescription}\n\nListen here: ${url}`)}`,
    
    messenger: `fb-messenger://share/?link=${encodeURIComponent(url)}`,
  };

  const socialPlatforms = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: shareLinks.whatsapp,
      color: '#25D366',
      gradient: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: shareLinks.facebook,
      color: '#1877F2',
      gradient: 'linear-gradient(135deg, #1877F2 0%, #0C63D4 100%)'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: shareLinks.twitter,
      color: '#1DA1F2',
      gradient: 'linear-gradient(135deg, #1DA1F2 0%, #0C85D0 100%)'
    },
    {
      name: 'Telegram',
      icon: Send,
      url: shareLinks.telegram,
      color: '#0088cc',
      gradient: 'linear-gradient(135deg, #0088cc 0%, #006699 100%)'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: shareLinks.linkedin,
      color: '#0077B5',
      gradient: 'linear-gradient(135deg, #0077B5 0%, #005885 100%)'
    },
    {
      name: 'Email',
      icon: Mail,
      url: shareLinks.email,
      color: '#EA4335',
      gradient: 'linear-gradient(135deg, #EA4335 0%, #C5221F 100%)'
    }
  ];

  const handleShare = (url: string, platform: string) => {
    // For email, use window.location.href to support iOS
    if (platform === 'Email') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="share-modal-header">
          <div className="share-modal-title-section">
            <div className="share-modal-icon">
              <Share2 size={24} />
            </div>
            <h2 className="share-modal-title">Share this Naat</h2>
          </div>
          <button className="share-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Naat Info */}
        <div className="share-modal-info">
          <h3 className="share-naat-title">{title}</h3>
          {artist && <p className="share-naat-artist">by {artist}</p>}
        </div>

        {/* Success Message */}
        {shareSuccess && (
          <div className="share-success-message">
            <Check size={20} />
            <span>Shared successfully!</span>
          </div>
        )}

        {/* Native Share (Mobile) */}
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <button className="share-native-btn" onClick={handleNativeShare}>
            <Share2 size={20} />
            <span>Share via...</span>
          </button>
        )}

        {/* Social Media Grid */}
        <div className="share-social-grid">
          {socialPlatforms.map((platform) => (
            <button
              key={platform.name}
              className="share-social-btn"
              onClick={() => handleShare(platform.url, platform.name)}
              style={{ '--platform-color': platform.color } as React.CSSProperties}
            >
              <div 
                className="share-social-icon"
                style={{ background: platform.gradient }}
              >
                <platform.icon size={24} />
              </div>
              <span className="share-social-name">{platform.name}</span>
            </button>
          ))}
        </div>

        {/* Copy Link Section */}
        <div className="share-copy-section">
          <div className="share-copy-label">Or copy link</div>
          <div className="share-copy-container">
            <input
              type="text"
              value={url}
              readOnly
              className="share-copy-input"
            />
            <button
              className={`share-copy-btn ${copied ? 'share-copied' : ''}`}
              onClick={handleCopyLink}
            >
              {copied ? (
                <>
                  <Check size={18} />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={18} />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
